import { Button } from "@components/ui/Button"
import { Icon, IconRef } from "@components/ui/Icon"
import { Popover } from "@components/ui/Popover"
import { useFerve, useFerveSession, useFerveUserId } from "@features/ferve/FerveProvider"
import { useResolvedPlanner } from "@features/ferve/useResolvedPlanner"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { CalendarDateRangeIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import { useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useState } from "react"
import { usePlannerSharing } from "./usePlannerSharing"
import { createPortal } from "react-dom"

type Props = {}

export function WishlistShareButton(props: Props) {
  const sharing = usePlannerSharing()
  const carpet = useFlyingCarpet()
  const [open, setOpen] = useState(false)
  const router = useRouter()

  const items = useResolvedPlanner()

  useEffect(() => {
    if (!carpet.isOpen || carpet.tab !== "planner" || sharing.isActive) {
      setOpen(false)
    }
  }, [carpet.tab, carpet.isOpen, sharing.isActive])

  const hasSelectedAll =
    sharing.isActive && items.every((item) => sharing.selectedItems.has(item.session.info?.ferveId!))

  const itemIds = items.map((item) => item.session.info?.ferveId!)
  const selectAll = () => sharing.setSelected(itemIds)

  const selectNone = () => sharing.setSelected([])

  if (!items.length) return null

  return (
    <>
      <AnimatePresence>
        {sharing.isActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute z-10 inset-0 bg-bg type-label-m"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0, transition: { delay: 0.2 } }}
              exit={{ opacity: 0, scale: 1, y: 0 }}
              className="absolute inset-0 px-[--padding] py-3 flex items-center justify-between"
            >
              {hasSelectedAll ? (
                <Button icon="radioCheck" variant="outline" onClick={selectNone}>
                  Deselect All
                </Button>
              ) : (
                <Button variant="outline" icon="radioCheck" onClick={selectAll}>
                  Select All
                </Button>
              )}
              <Button variant="outline" onClick={() => sharing.cancel()}>
                Cancel
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prevent tapping outside the popover from closing it */}
      {open ? createPortal(<div className="fixed z-[59] inset-0 !pointer-events-auto"></div>, document.body) : null}

      <Popover
        open={open}
        onOpenChange={setOpen}
        offsetDistance={-5}
        anchor={
          <Button
            variant="outline"
            onPointerDown={() => {
              if (open) {
                setOpen(false)
              } else {
                trackEvent("Open Planner Share Dialog", {}, null)
                setOpen(true)
              }
            }}
          >
            Share
          </Button>
        }
        padded={false}
      >
        <div className="flex flex-col gap-[4px] p-[6px]">
          <ShareOption
            icon="link"
            title="Send as link"
            description="Get a personalised link to share with friends."
            onSelect={() => {
              trackEvent("Wishlist: Share a Link", {}, null)
              sharing.start("link")
            }}
          />
          <ShareOption
            icon={<CalendarDateRangeIcon className="scale-75" />}
            title="Add to Calendar"
            description="Export to your calendar to keep track of what's coming up."
            onSelect={() => {
              trackEvent("Wishlist: Share to Calendar", {}, null)
              sharing.start("calendar")
            }}
          />
          <ShareOption
            icon={<DevicePhoneMobileIcon className="scale-75" />}
            title="Share to my Story"
            description="Let the world know what you'll be watching."
            onSelect={() => {
              trackEvent("Wishlist: Share to Story", {}, null)
              router.navigate("/share-to-story")
            }}
          />
        </div>
      </Popover>
      {/* <Dialog
        open={shareMode === "link"}
        onOpenChange={(open) => {
          if (!open) setShareMode(null)
        }}
        title="Share your Wishlist"
      >
        <div className="flex gap-2">
          <Input
            readOnly
            value="https://sff.org.au/"
            className="flex-1"
            onClick={(e) => {
              e.currentTarget.select()
            }}
          />
        </div>
      </Dialog> */}
    </>
  )
}

type ShareOptionProps = {
  icon: IconRef
  title: string
  description: string
  onSelect: () => void
}

function ShareOption(props: ShareOptionProps) {
  return (
    <button
      className="flex gap-2 border border-bg-low active:border-bg-mid/50 active:bg-bg-low active:border-bg-mid md:hover:bg-bg-low md:hover:border-bg-mid/20 p-[8px] items-center rounded-md transition-colors text-left max-w-[230px]"
      onClick={props.onSelect}
    >
      <div className="w-icon flex-none">
        <Icon name={props.icon} />
      </div>
      <div className="flex flex-1 flex-col">
        <span className="type-ui-m mb-[0.2em] mt-[0.1em]">{props.title}</span>
        <span className="type-label-s text-pretty leading-[1.5em] text-fg-secondary">{props.description}</span>
      </div>
    </button>
  )
}
